import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { ArrowLeft, Timer, TrendingUp, Maximize, Minimize, Coins, RefreshCw, Trophy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/components/ui/use-toast";
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from "@/components/ui/alert-dialog";
import { usePokerStore } from "@/features/poker/poker-store";
import { usePokerWS } from "@/features/poker/hooks/usePokerWS";
import { apiAct, apiLeave, apiReady, apiRebuy, apiState } from "@/features/poker/api";
import { PokerTopBar } from "@/features/poker/components/PokerTopBar";
import { PokerPlayers } from "@/features/poker/components/PokerPlayers";
import { PokerActionDock } from "@/features/poker/components/PokerActionDock";
import { PrizeInfoOverlay } from "@/features/poker/components/PrizeInfoOverlay";
import { CoachOverlay } from "@/features/learn/components/CoachOverlay";
import { useAuthStore } from "@/features/auth/auth-store";
import { apiGetMe } from "@/features/auth/api";
import { GameState, Player } from "@/types/poker";

const COACH_MAX_HANDS = 5;

export const PokerTable = () => {
  const { t } = useTranslation();
  const { toast } = useToast();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { room, token, playerId, clearSession } = usePokerStore();
  const authToken = useAuthStore((s) => s.token);
  const updateUser = useAuthStore((s) => s.updateUser);

  const [state, setState] = useState<GameState | null>(null);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [isFullscreen, setIsFullscreen] = useState(false);
  const [eliminated, setEliminated] = useState(false);
  const [showLeaveConfirm, setShowLeaveConfirm] = useState(false);
  const wasInGameRef = useRef(false);
  const leavingRef = useRef(false);
  const eliminationTimerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  // --- Coach guided practice ---
  const guidedLessonId = useMemo(() => {
    const fromUrl = searchParams.get("guided");
    if (fromUrl) return fromUrl;
    try {
      const stored = localStorage.getItem("coach-guided-practice");
      if (stored) {
        const parsed = JSON.parse(stored);
        if (parsed?.guided && parsed?.lessonId) return parsed.lessonId as string;
      }
    } catch { /* ignore */ }
    return null;
  }, [searchParams]);

  const [coachHandsPlayed, setCoachHandsPlayed] = useState(0);
  const coachPrevPhaseRef = useRef<string | null>(null);

  // Track hand transitions for the coach overlay
  useEffect(() => {
    if (!guidedLessonId || !state) return;
    const prev = coachPrevPhaseRef.current;
    coachPrevPhaseRef.current = state.phase;

    // Count a new hand when transitioning to "preflop" from idle/showdown
    if (
      state.phase === "preflop" &&
      (prev === "idle" || prev === "showdown")
    ) {
      setCoachHandsPlayed((h) => h + 1);
    }
  }, [state, guidedLessonId]);

  const handleCoachDismiss = useCallback(() => {
    localStorage.removeItem("coach-guided-practice");
    // Leave table and go to lobby
    if (token && room) apiLeave({ token, room }).catch(() => {});
    clearSession();
    if (authToken) {
      apiGetMe(authToken).then((data) => updateUser({ chips: data.chips })).catch(() => {});
    }
    navigate("/play");
  }, [token, room, clearSession, authToken, navigate, updateUser]);

  // Estados locales para contadores fluidos
  const [nextLevelTime, setNextLevelTime] = useState(0);
  const [turnProgress, setTurnProgress] = useState(0);
  const [turnSeconds, setTurnSeconds] = useState(0);

  // Inicialización
  useEffect(() => {
    if (!token || !room) {
      navigate("/play", { replace: true });
      return;
    }

    apiState({ token, room })
      .then(setState)
      .catch((err) => {
        console.error("[PokerTable] Failed to load table state:", err);
        setLoadError(err instanceof Error ? err.message : "Failed to load table");
      })
      .finally(() => setIsLoading(false));

    // Fullscreen logic
    const handleFullscreenChange = () => setIsFullscreen(!!document.fullscreenElement);
    document.addEventListener("fullscreenchange", handleFullscreenChange);

    // Auto-attempt fullscreen (best effort)
    const tryFullscreen = async () => {
      try {
        if (!document.fullscreenElement && document.documentElement.requestFullscreen) {
          await document.documentElement.requestFullscreen();
        }
      } catch (e) {
        // Ignorar error de interacción de usuario requerida
      }
    };
    tryFullscreen();

    return () => document.removeEventListener("fullscreenchange", handleFullscreenChange);
    // Run once on mount only: this loads the initial table state and makes a single
    // best-effort fullscreen request. Adding token/room/navigate would re-run
    // apiState() and re-attempt requestFullscreen() (a user-gesture-gated API) on
    // every dependency change, not just on the initial mount — a real behaviour
    // change, not a false positive.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const toggleFullscreen = () => {
    if (!document.fullscreenElement) {
      document.documentElement.requestFullscreen().catch(() => {});
    } else {
      if (document.exitFullscreen) {
        document.exitFullscreen().catch(() => {});
      }
    }
  };

  const handleWSError = useCallback((error: Event) => {
      console.error("WS Error", error);
    }, []);

  const { connected } = usePokerWS({
    room,
    token,
    onState: setState,
    onError: handleWSError,
  });

  // Polling fallback when WS is disconnected
  useEffect(() => {
    if (!token || !room || connected) return;

    const poll = () => {
      apiState({ token, room })
        .then(setState)
        .catch((err) => console.error("[PokerTable] Polling error:", err));
    };

    poll();
    const interval = setInterval(poll, 3000);
    return () => clearInterval(interval);
  }, [connected, token, room]);

  const you = useMemo(() => state?.players?.find((p: Player) => p.id === playerId), [state, playerId]);
  const toCall = useMemo(() => {
    if (!state || !playerId || !state.roundBets) return 0;
    return state.currentBet - (state.roundBets[playerId] || 0);
  }, [state, playerId]);

  // --- LOGICA DE TEMPORIZADORES (Update cada segundo) ---
  useEffect(() => {
    if (!state) return;

    const tick = () => {
      const now = Date.now() / 1000; // Segundos

      // 1. Reloj de Nivel
      if (state.isTournament && state.levelStartedAt && state.levelDurationSeconds) {
        const elapsed = now - state.levelStartedAt;
        const remaining = Math.max(0, state.levelDurationSeconds - elapsed);
        setNextLevelTime(Math.floor(remaining));
      }

      // 2. Reloj de Turno (Barra de progreso + segundos)
      if (state.turnExpiresAt && state.turnExpiresAt > 0) {
        const timeLeft = Math.max(0, state.turnExpiresAt - now);
        const percentage = Math.min(100, (timeLeft / 15) * 100);
        setTurnProgress(percentage);
        setTurnSeconds(Math.ceil(timeLeft));
      } else {
        setTurnProgress(0);
        setTurnSeconds(0);
      }
    };

    tick(); // Ejecutar inmediatamente
    const interval = setInterval(tick, 200); // 5 fps para suavidad en la barra
    return () => clearInterval(interval);
  }, [state]); // Se reinicia cuando llega un nuevo estado del servidor

  // --- Detect elimination (tournament only) ---
  useEffect(() => {
    if (!state || !playerId) return;
    if (state.gameMode === "cash") return; // Cash games don't eliminate

    const youInGame = state.players.some((p: Player) => p.id === playerId);

    if (
      wasInGameRef.current &&
      !youInGame &&
      !eliminated &&
      !leavingRef.current &&
      !eliminationTimerRef.current
    ) {
      setEliminated(true);
      toast({
        title: t('poker_eliminated'),
        description: t('poker_eliminated_desc'),
      });
      // Auto-leave after a short delay. `state` is replaced on every WS
      // broadcast (see the ref-guard note below), so this timer is NOT
      // cleared/rescheduled when the effect re-runs — only unmount clears it
      // (see the effect right after this one). If it were torn down on every
      // effect re-run instead, the very first broadcast that arrives inside
      // the 4s window would cancel it, and because `!eliminated` gates
      // re-scheduling, it would then never fire again — H6.
      eliminationTimerRef.current = setTimeout(() => {
        eliminationTimerRef.current = null;
        clearSession();
        if (authToken) {
          apiGetMe(authToken).then((data) => updateUser({ chips: data.chips })).catch(() => { /* eliminated, best effort */ });
        }
        navigate("/play");
      }, 4000);
    }

    if (youInGame) {
      wasInGameRef.current = true;
    }
    // `state` is deliberately the only real trigger here (elimination is
    // detected by diffing `state.players` against `wasInGameRef`) — but note
    // `state` is replaced on *every* WebSocket broadcast, including ones that
    // arrive while the 4s auto-leave timer above is counting down. The
    // `eliminationTimerRef.current` guard is what makes that safe: once the
    // timer is scheduled, a broadcast re-running this effect sees the ref
    // already set and skips scheduling again, so the one-shot timer survives
    // untouched instead of being torn down and never rescheduled (H6). Adding
    // `eliminated` to the deps would be redundant with that guard, not
    // required by it.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [state, playerId]);

  // The elimination auto-leave timer (scheduled above) must survive every
  // intermediate re-render/broadcast while its 4s countdown is running — it
  // is cleared on unmount only, never as a per-render effect cleanup.
  useEffect(() => {
    return () => {
      if (eliminationTimerRef.current) {
        clearTimeout(eliminationTimerRef.current);
        eliminationTimerRef.current = null;
      }
    };
  }, []);

  const handleRebuy = async () => {
    if (!token || !room || !state) return;
    try {
      await apiRebuy({ token, room, amount: state.maxBuyIn, authToken: authToken || undefined });
      if (authToken) {
        apiGetMe(authToken).then((data) => updateUser({ chips: data.chips })).catch(() => {});
      }
    } catch (err) {
      toast({ title: "Rebuy failed", description: err instanceof Error ? err.message : "Try again", variant: "destructive" });
    }
  };

  // --- HANDLERS ---
  const handleLeaveTable = async () => {
    leavingRef.current = true;
    if (token && room) {
      try {
        await apiLeave({ token, room });
      } catch {
        // best effort — we're leaving regardless
      }
    }
    clearSession();
    // Refresh auth chip balance
    if (authToken) {
      apiGetMe(authToken).then((data) => updateUser({ chips: data.chips })).catch(() => { /* leaving, best effort */ });
    }
    navigate("/play");
  };

  const handleReady = async () => {
    if (token && room) {
      try { await apiReady({ token, room }); }
      catch (err) { toast({ title: "Error", description: err instanceof Error ? err.message : "Failed to ready up", variant: "destructive" }); }
    }
  };

  const actingRef = useRef(false);
  const handlePlayerAct = async (action: string, amount?: number) => {
    if (!token || !room || actingRef.current) return;
    actingRef.current = true;
    try { await apiAct({ token, room, action, amount }); }
    catch (err) { toast({ title: "Action failed", description: err instanceof Error ? err.message : "Try again", variant: "destructive" }); }
    finally { actingRef.current = false; }
  };

  if (isLoading) return <div className="flex h-screen items-center justify-center text-muted-foreground">{t('poker_loading')}</div>;

  if (loadError || !state) return (
    <div className="flex h-screen flex-col items-center justify-center gap-4 px-4 text-center">
      <p className="text-lg font-semibold text-destructive">{loadError || t('poker_error_load')}</p>
      <div className="flex gap-2">
        <Button variant="secondary" onClick={() => {
          setLoadError(null);
          setIsLoading(true);
          apiState({ token: token!, room: room! })
            .then(setState)
            .catch((err) => setLoadError(err instanceof Error ? err.message : "Failed to load table"))
            .finally(() => setIsLoading(false));
        }}>
          {t('poker_retry')}
        </Button>
        <Button variant="outline" onClick={() => { clearSession(); navigate("/play"); }}>
          {t('poker_back_lobby')}
        </Button>
      </div>
    </div>
  );

  const formatTime = (s: number) => {
    const m = Math.floor(s / 60);
    const sec = Math.floor(s % 60);
    return `${m}:${sec.toString().padStart(2, "0")}`;
  };

  // Identificar jugador activo para ponerle la barra de tiempo
  const activePlayerId = state.actionIndex != null && state.actionIndex >= 0
    ? state.players[state.actionIndex]?.id
    : null;

  return (
    <div className="pt-12 min-h-screen bg-background relative">
      {/* Barra de progreso de turno global (opcional) o sobre el jugador */}
      {turnProgress > 0 && (
         <div className="fixed top-0 left-0 w-full z-50">
             <div className="h-1 bg-secondary">
               <div
                 className="h-full bg-primary transition-all duration-200 linear"
                 style={{ width: `${turnProgress}%` }}
               />
             </div>
             <div className="absolute right-2 top-1 text-xs font-mono font-bold text-primary tabular-nums">
               {turnSeconds}s
             </div>
         </div>
      )}

      <div className="relative mx-auto flex w-full max-w-5xl flex-col gap-4 px-4 py-3">
        {/* Room/pot/phase summary + ready-up control (hidden by PokerTopBar itself
            once phase leaves "idle") and leave action. */}
        <PokerTopBar
          state={state}
          you={you}
          connected={connected}
          onReady={handleReady}
          onLeave={() => {
            if (state.gameMode === "cash") {
              setShowLeaveConfirm(true);
            } else {
              handleLeaveTable();
            }
          }}
        />

        {/* TABLE HUD */}
        <div className="flex w-full items-center gap-2">
          <Button variant="ghost" size="icon" className="-ml-2 shrink-0" onClick={() => {
            if (state.gameMode === "cash") {
              setShowLeaveConfirm(true);
            } else {
              handleLeaveTable();
            }
          }}>
            <ArrowLeft className="h-5 w-5" />
          </Button>

          {state.isTournament ? (
            <div className="flex flex-1 items-center justify-between rounded-lg bg-secondary/30 px-6 py-2 text-sm text-secondary-foreground">
              <div className="flex items-center gap-2">
                <TrendingUp className="h-4 w-4 text-primary" />
                <span className="font-bold">{t('poker_level', { level: state.levelIndex })}</span>
              </div>
              <div className="font-mono">
                {state.sb}/{state.bb} {state.ante > 0 && ` - ${state.ante})`}
              </div>
              <div className="flex items-center gap-2">
                <Timer className="h-4 w-4 text-primary" />
                <span className="tabular-nums">{formatTime(nextLevelTime)}</span>
              </div>
            </div>
          ) : (
            <div className="flex flex-1 items-center justify-between rounded-lg bg-green-900/40 px-6 py-2 text-sm text-green-100">
              <div className="flex items-center gap-2">
                <Coins className="h-4 w-4 text-green-400" />
                <span className="font-bold">{t('poker_cash_game')}</span>
              </div>
              <div className="font-mono font-bold">
                {state.sb}/{state.bb}
              </div>
            </div>
          )}

          <Button variant="ghost" size="icon" className="ml-auto shrink-0" onClick={toggleFullscreen}>
            {isFullscreen ? <Minimize className="h-5 w-5" /> : <Maximize className="h-5 w-5" />}
          </Button>
        </div>

        {/* Cash game rebuy prompt */}
        {state.gameMode === "cash" && you && you.stack === 0 && state.phase === "idle" && (
          <div className="rounded-lg bg-secondary/20 border border-secondary/30 p-4 text-center">
            <p className="text-sm text-muted-foreground mb-2">{t('poker_out_of_chips')}</p>
            <Button onClick={handleRebuy} className="bg-secondary text-secondary-foreground hover:bg-secondary/90">
              <RefreshCw className="mr-2 h-4 w-4" />
              {t('poker_rebuy', { amount: state.maxBuyIn.toLocaleString() })}
            </Button>
          </div>
        )}

        <div className="flex flex-col gap-2">
          <div className="relative w-full">
            {state.phase === "idle" && state.isTournament && (
              <PrizeInfoOverlay maxPlayers={state.maxPlayers} currentPlayers={state.players.length} />
            )}
            {/* Pasamos activePlayerId y turnProgress a PokerPlayers para que renderice el timer individualmente si quieres */}
            <PokerPlayers state={state} playerId={playerId!} />
          </div>

          <PokerActionDock
            state={state}
            you={you}
            toCall={toCall}
            onAct={handlePlayerAct}
          />
        </div>
      </div>

      {/* Tournament winner overlay */}
      {state?.tournamentFinished && !eliminated && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="rounded-xl bg-card p-8 text-center shadow-2xl max-w-sm mx-4">
            <Trophy className="h-16 w-16 text-secondary mx-auto mb-4" />
            <h2 className="text-3xl font-bold text-secondary mb-2">
              {state.tournamentWinner?.id === playerId ? t('poker_you_won') : t('poker_player_wins', { name: state.tournamentWinner?.name })}
            </h2>
            <div className="flex items-center justify-center gap-2 text-xl mb-6">
              <Coins className="h-6 w-6 text-secondary" />
              <span className="font-bold text-secondary">+{state.tournamentPrize?.toLocaleString()}</span>
              <span className="text-muted-foreground">{t('poker_chips')}</span>
            </div>
            <Button
              size="lg"
              className="w-full bg-secondary text-secondary-foreground hover:bg-secondary/90"
              onClick={() => {
                if (token && room) apiLeave({ token, room }).catch(() => {});
                clearSession();
                if (authToken) {
                  apiGetMe(authToken).then((data) => updateUser({ chips: data.chips })).catch(() => {});
                }
                navigate("/play");
              }}
            >
              {t('poker_back_lobby')}
            </Button>
          </div>
        </div>
      )}

      {/* Cash game leave confirmation */}
      <AlertDialog open={showLeaveConfirm} onOpenChange={setShowLeaveConfirm}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>{t('poker_leave_title')}</AlertDialogTitle>
            <AlertDialogDescription>{t('poker_leave_desc')}</AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>{t('cancel')}</AlertDialogCancel>
            <AlertDialogAction onClick={handleLeaveTable}>
              {t('poker_leave_confirm')}
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>

      {/* Coach guided practice overlay */}
      {guidedLessonId && state && playerId && (
        <CoachOverlay
          lessonId={guidedLessonId}
          gameState={state}
          playerId={playerId}
          onDismiss={handleCoachDismiss}
          handsPlayed={coachHandsPlayed}
          maxHands={COACH_MAX_HANDS}
        />
      )}

      {/* Elimination overlay (tournament only) */}
      {eliminated && state?.gameMode !== "cash" && (
        <div className="fixed inset-0 z-[100] flex items-center justify-center bg-black/70 backdrop-blur-sm">
          <div className="rounded-xl bg-card p-8 text-center shadow-2xl max-w-sm mx-4">
            <h2 className="text-2xl font-bold text-destructive mb-2">{t('poker_eliminated')}</h2>
            <p className="text-muted-foreground mb-6">
              {t('poker_eliminated_returning')}
            </p>
            <Button onClick={() => { clearSession(); navigate("/play"); }} className="w-full">
              {t('poker_back_lobby')}
            </Button>
          </div>
        </div>
      )}
    </div>
  );
};
